import { gql } from "@apollo/client";

/**
 * Shared helpers + GraphQL operations for the Chat Automations tools.
 * The operations map 1:1 to the admin-only resolver in
 * `api/src/chat/automations/chatAutomations.resolver.ts`.
 */

/**
 * The server rejects anything larger (see MAX_BULK_RECIPIENTS in
 * api/src/chat/automations/chatAutomations.input.ts). Kept here too so an
 * oversized upload is caught while it is still a file, with a sentence that
 * explains itself, rather than as a validation error after pressing send.
 */
export const MAX_BULK_RECIPIENTS = 500;
/** Stream refuses longer message text, so there is no point letting one be typed. */
export const MAX_MESSAGE_LENGTH = 5000;

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

/** Pull unique, lower-cased email addresses out of an uploaded CSV's text. */
export const extractEmails = (text: string): string[] => {
  const found = text.match(EMAIL_PATTERN) ?? [];
  return Array.from(new Set(found.map((email) => email.toLowerCase())));
};

export interface MatchedMember {
  id: string;
  email: string;
  firstName?: string | null;
}

export interface MatchMembersResult {
  matched: MatchedMember[];
  notFound: string[];
}

export interface BulkActionResult {
  total: number;
  succeeded: number;
  failed: number;
  notFound: string[];
  /** Present once the run is queued — poll SEND_PROGRESS with it. */
  sendId?: string | null;
}

export interface BulkSendProgress {
  id: string;
  status: "queued" | "running" | "done" | "failed";
  total: number;
  sent: number;
  failed: number;
  pending: number;
  error?: string | null;
}

/** How often the screen asks the server how a run is going. */
export const PROGRESS_POLL_MS = 2000;

export const MATCH_MEMBERS = gql`
  query ChatAutomationsMatchMembers($input: MatchMembersInput!) {
    chatAutomationsMatchMembers(input: $input) {
      matched {
        id
        email
        firstName
      }
      notFound
    }
  }
`;

export const SEND_MESSAGES = gql`
  mutation ChatAutomationsSendMessages($input: SendBulkMessagesInput!) {
    chatAutomationsSendMessages(input: $input) {
      total
      succeeded
      failed
      notFound
      skipped
      sendId
    }
  }
`;

/** One bulk send, as the Recent Sends list shows it. */
export interface SentBatch {
  batchId: string;
  campaign?: string | null;
  messagePreview?: string | null;
  sentAt: string;
  memberCount: number;
  recallableCount: number;
  recalledAt?: string | null;
}

export interface RecallResult {
  deleted: number;
  failed: number;
  /** Still to take back — one press is capped, so press again to continue. */
  remaining: number;
}

export const RECENT_SENDS = gql`
  query ChatAutomationsRecentSends {
    chatAutomationsRecentSends {
      batchId
      campaign
      messagePreview
      sentAt
      memberCount
      recallableCount
      recalledAt
    }
  }
`;

export const RECALL_SEND = gql`
  mutation ChatAutomationsRecallSend($batchId: String!) {
    chatAutomationsRecallSend(batchId: $batchId) {
      deleted
      failed
      remaining
    }
  }
`;

export const ADD_MEMBERS = gql`
  mutation ChatAutomationsAddMembers($input: AddBulkMembersInput!) {
    chatAutomationsAddMembers(input: $input) {
      total
      succeeded
      failed
      notFound
      skipped
      sendId
    }
  }
`;

export const SEND_PROGRESS = gql`
  query ChatAutomationsSendProgress($sendId: String!) {
    chatAutomationsSendProgress(sendId: $sendId) {
      id
      status
      total
      sent
      failed
      pending
      error
    }
  }
`;
